import styled from "styled-components";

import { controlFocusRing, controlTransition } from "../../../foundations/controlStyles";
import type { CalendarDayCell } from "../useCalendarMonth";
import { CELL } from "./calendarStyles";

export interface DayCellProps {
  /** 이 셀이 그릴 날짜 + 상태 플래그(useCalendarMonth 산출). 어떤 숫자를 그릴지는 여기서 결정된다. */
  cell: CalendarDayCell;
  /** 선택된 끝(단일 선택일/범위 양 끝) — primary fill. */
  endpoint: boolean;
  /** 양 끝 사이(또는 hover 프리뷰) 밴드. */
  inRange: boolean;
  /** roving tabindex 대상(=포커스 가능). 나머지는 tabIndex -1. */
  focusTarget: boolean;
  onSelect: (date: Date) => void;
  onFocus: (date: Date) => void;
  /** 범위 hover 프리뷰용(지정 시에만 mouseenter로 hover 셀 보고). 단일 선택에선 미전달. */
  onHover?: (date: Date) => void;
  /** 실제 버튼 엘리먼트를 roving tabindex 포커스 이동용으로 등록/해제. */
  registerRef: (date: Date, el: HTMLButtonElement | null) => void;
}

/**
 * 달력 day 셀(프레젠테이셔널). "어떤 날짜/상태인지"는 전부 props가 정하고, 이 컴포넌트는 그리기만
 * 책임진다. 현재 달이 아닌(leading/trailing) 칸은 빈 placeholder. endpoint/inRange/today가 겹치는
 * 다상태라 interactiveSurfaceCss 대신 공유 파운데이션 + semantic 토큰으로 전개(§5-C 이탈 근거 아래).
 */
export const DayCell = ({
  cell,
  endpoint,
  inRange,
  focusTarget,
  onSelect,
  onFocus,
  onHover,
  registerRef,
}: DayCellProps) => {
  if (!cell.isCurrentMonth) {
    return <Empty role="gridcell" aria-hidden="true" />;
  }
  return (
    <CellRoot role="gridcell" $inRange={inRange}>
      <DayButton
        type="button"
        ref={(el) => registerRef(cell.date, el)}
        tabIndex={focusTarget ? 0 : -1}
        disabled={cell.isDisabled}
        aria-label={cell.date.toDateString()}
        aria-selected={endpoint}
        aria-current={cell.isToday ? "date" : undefined}
        $endpoint={endpoint}
        $inRange={inRange}
        $today={cell.isToday}
        onClick={() => onSelect(cell.date)}
        onFocus={() => onFocus(cell.date)}
        onMouseEnter={onHover ? () => onHover(cell.date) : undefined}
      >
        {cell.date.getDate()}
      </DayButton>
    </CellRoot>
  );
};

const Empty = styled.div`
  width: ${CELL};
  height: ${CELL};
`;

// in-range 밴드는 셀 배경으로 그려 연속감을 준다(양 끝 제외 셀은 사각 밴드).
const CellRoot = styled.div<{ $inRange: boolean }>`
  width: ${CELL};
  height: ${CELL};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme, $inRange }) =>
    $inRange ? theme.color.bg.control.primarySubtle : "transparent"};
`;

// 규칙 이탈 근거(component-authoring §5-C): day 셀은 interactiveSurfaceCss(rest/hover/active/
// disabled 4-slot)로 표현 불가 — endpoint(hover와 무관한 primary fill)·in-range·today가 겹치는
// 다상태라 4-slot 스키마를 벗어난다. 대신 공유 파운데이션(controlFocusRing/controlTransition) +
// semantic 토큰으로 상태를 전개해 focus/transition 메커니즘은 동일하게 유지한다.
const DayButton = styled.button<{ $endpoint: boolean; $inRange: boolean; $today: boolean }>`
  width: calc(${CELL} - 0.25rem);
  height: calc(${CELL} - 0.25rem);
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: ${({ theme }) =>
    theme.shape.radius.control}; /* 둥근 사각형(선택=fill, 오늘=링, hover=옅은 면) */
  font: inherit;
  font-size: ${({ theme }) => theme.typography.size.body};
  color: ${({ theme, $endpoint }) =>
    $endpoint ? theme.color.text.onControl : theme.color.text.default};
  background: ${({ theme, $endpoint }) =>
    $endpoint ? theme.color.bg.control.primary : "transparent"};
  box-shadow: ${({ theme, $today, $endpoint }) =>
    $today && !$endpoint ? `inset 0 0 0 1px ${theme.color.border.selected}` : "none"};
  cursor: pointer;
  ${controlTransition("background-color", "color", "transform")}

  &:not(:disabled):hover {
    /* endpoint=solid hover, in-range=밴드 계열 한 단계 진하게(중립 surface로 덮지 않음),
       그 외=중립 surfaceRaisedHigh(선택 안 된 셀의 뚜렷한 hover). */
    background: ${({ theme, $endpoint, $inRange }) =>
      $endpoint
        ? theme.color.bg.control.primaryHover
        : $inRange
          ? theme.color.bg.control.primarySubtleHover
          : theme.color.bg.surfaceRaisedHigh};
    /* hover 시 살짝 위로 떠오르는 lift 피드백. reduced-motion에서는 아래 미디어쿼리로 제거. */
    transform: translateY(-2px);
  }
  &:focus-visible {
    ${controlFocusRing()}
  }
  &:disabled {
    color: ${({ theme }) => theme.color.text.disabled};
    cursor: not-allowed;
  }

  /* 장식 모션이므로 reduced-motion에서 전이·확대 모두 제거(§5-C). */
  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &:not(:disabled):hover {
      transform: none;
    }
  }
`;
